<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref, computed } from "vue";
import type { DeclaredUnitEnum } from "@/types/Views/MaterialDatabase/MaterialEnums";

import { useProject } from "@/stores/project/project.store";
import { useEnum } from "@/stores/project/project.enum.ts";

import GroupMaterialRow from "./GroupMaterialRow.vue";

import { addMappingMaterial, UpdateMaterialPropertiesRequest } from "@/api/services/Mapping";
import { classificationUserParameters } from "./classificationUserParameters";

import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import { Plus, Recycle } from "@lucide/vue";
import DropDown from "@/components/Reusables/Inputs/DropDown.vue";

import Button from "@/components/Reusables/Buttons/Button.vue";

import handleApiError from "@/services/Utils/ErrorHandler";
import Divider from "@/components/Reusables/Divider/Divider.vue";
import Switch from "@/components/Reusables/Switches/Switch.vue";
import { useModalStore } from "@/stores/modals/modals.store";
import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { Material } from "@/types/Views/MaterialDatabase/Material";
import { Country_Enum, useLocalization } from "@/providers/Localization/index.ts";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";

const enumStore = useEnum();
const { declaredUnits } = storeToRefs(enumStore);

const dashboardStore = useDashboard();
const { fetchDashboard } = dashboardStore;

const projectStore = useProject();
const { fetchProjectMaterials, setInventoryFocus } = projectStore;
// Every model, not just the Speckle branches — a manually created material belongs to whichever
// model the user picks, including one pushed from APS/Forma (RTLCA-5291).
const { selectedModel, project, isGroupingByType, allModelNames } = storeToRefs(projectStore);

const localization = useLocalization();
const { country } = storeToRefs(localization);

// Countries that have their own built-in lifetime table (rendered via the legacy
// `LifetimeTable` modal) rather than the new buildingPartServiceLifetime picker.
const COUNTRIES_WITHOUT_LIFETIME_PICKER = [Country_Enum.DENMARK];

// Lifespan bounds, matching every other lifetime input in the app (see MaterialMappingForm /
// MaterialBasicsSection): a lifetime of 0 counts as "missing", and 300 years is the ceiling.
const MIN_LIFESPAN_YEARS = 1;
const MAX_LIFESPAN_YEARS = 300;

const props = defineProps<{
  prefillMaterial?: Material;
}>();

const groupIsRecycled = ref<boolean>(false);

const groupIdentifier = ref(props.prefillMaterial?.name ?? "");
const groupBIMTypeCode = ref("0");
// A dropped library material reporting 0 has no lifespan of its own, so start the field empty
// rather than prefilling a value nobody chose — the user has to enter one.
const expectedLifespan = ref<number | null>(
  props.prefillMaterial?.expectedLifespan ? props.prefillMaterial.expectedLifespan : null
);
// One model, never several: picking two used to fan the payload out into a separate, independent
// material per model — same name, no link between them (RTLCA-5392).
const selectedModelName = ref<string | null>(selectedModel.value ?? null);

const materials = ref([
  {
    typeIdentifier: "",
    materialIdentifier: "",
    includeMapping: true,
    branchName: selectedModel.value!,
    isRecycled: false,
    tags: [],
    lifetime: 0,
    materialCount: 1,
    declaredUnitValuePairs: [
      {
        declaredUnit: props.prefillMaterial?.declaredUnit.declaredUnit ?? -1,
        value: props.prefillMaterial?.declaredUnit.declaredValue ?? null,
        preferred: true,
      },
    ],
    speckleUserParameters: {
      BIMTypeCode: "",
      CCSClassification: "",
      classificationObjectClass: "",
    },
    stage: 0,
    description: "",
  },
]);

const identifierAlreadyExists = ref<boolean>();
const hasValidated = ref<boolean>(false);
const isSaving = ref<boolean>(false);

const modelErrorMsg = computed(() => (hasValidated.value && !selectedModelName.value ? "Select a model" : undefined));

// The lifespan input is only shown when a library material is dropped in — an unmapped material
// takes its lifetime from the EPD it is mapped to later, so there is nothing to require yet.
const isLifespanValid = computed(
  () => !props.prefillMaterial || (expectedLifespan.value != null && expectedLifespan.value >= MIN_LIFESPAN_YEARS)
);

// The Create button is disabled while the form is invalid, so an error gated on a submit attempt
// alone could never surface. Flag the field as touched on the first edit instead, so clearing it
// or entering 0 reports itself straight away.
const lifespanTouched = ref<boolean>(false);

const updateExpectedLifespan = (lifespan: number | null) => {
  expectedLifespan.value = lifespan;
  lifespanTouched.value = true;
};

const lifespanErrorMsg = computed(() =>
  (hasValidated.value || lifespanTouched.value) && !isLifespanValid.value
    ? `Enter a lifespan of at least ${MIN_LIFESPAN_YEARS} year`
    : undefined
);

const isFormValid = computed(() => {
  if (groupIdentifier.value.length === 0 || !selectedModelName.value) return false;
  if (!isLifespanValid.value) return false;
  return materials.value.every((m) => {
    const pair = m.declaredUnitValuePairs[0];
    return (
      m.typeIdentifier.length > 0 &&
      m.speckleUserParameters.CCSClassification.length > 0 &&
      pair?.declaredUnit !== -1 &&
      pair?.value != null &&
      pair.value >= 0
    );
  });
});

const emits = defineEmits<{
  toggleModal: [];
}>();

const createChild = () => {
  const newChild: any = {
    typeIdentifier: "",
    materialIdentifier: "",
    includeMapping: true,
    branchName: selectedModel.value!,
    tags: [],
    isRecycled: false,
    lifetime: 0,
    materialCount: 1,
    declaredUnitValuePairs: [{ declaredUnit: -1, value: null, preferred: true }],
    speckleUserParameters: {
      BIMTypeCode: "",
      CCSClassification: "",
      classificationObjectClass: "",
    },
    stage: 0,
    description: "",
  };

  materials.value.push(newChild);
  hasValidated.value = false;
};

// The openers (the "New" menu, the create-group dropzone, the empty state) are already closed for a
// Viewer and while a frozen revision is previewed; this covers a form that was already open when
// the revision or the role changed — a create would land on the working state, not the revision on
// screen, and a Viewer's would be refused by Hasura (RTLCA-5253, RTLCA-5582).
const { blockedByPreview } = useProjectEditReadOnly();

const submit = async () => {
  if (blockedByPreview()) return;

  hasValidated.value = true;

  const isChildrenInvalid = materials.value.some((m) => {
    const pair = m.declaredUnitValuePairs[0];
    return (
      m.typeIdentifier.length === 0 ||
      m.speckleUserParameters.CCSClassification.length === 0 ||
      pair?.declaredUnit === -1 ||
      pair?.value === null ||
      pair?.value === undefined ||
      pair?.value < 0
    );
  });

  if (isChildrenInvalid || groupIdentifier.value.length === 0 || !selectedModelName.value || !isLifespanValid.value) {
    return;
  }

  const baseRows = materials.value.map((material) => ({
    ...material,
    lifetime: expectedLifespan.value ?? 0,
    materialIdentifier: groupIdentifier.value,
    // Each row is its own type with its own code (GroupMaterialRow writes it to CCSClassification
    // for every classification system), so the payload is built per row. It used to stamp every row
    // with the group-level groupBIMTypeCode instead — the last plain BIM code picked, and empty for
    // CCI/CCS/Uniclass, whose codes never reach that ref (RTLCA-5774).
    speckleUserParameters: {
      ...material.speckleUserParameters,
      ...classificationUserParameters(
        material.speckleUserParameters.CCSClassification,
        material.speckleUserParameters.classificationObjectClass
      ),
    },
    isRecycled: groupIsRecycled.value,
  }));

  const payload: any = baseRows.map((row) => ({ ...row, branchName: selectedModelName.value }));

  isSaving.value = true;

  try {
    const createdMaterials = await addMappingMaterial(project.value!.id, payload);

    hasValidated.value = false;

    if (props.prefillMaterial?.id && Array.isArray(createdMaterials) && createdMaterials.length > 0) {
      const directPayload = (createdMaterials as unknown as string[]).map((id, i) => {
        const row = payload[i];
        const pair = row?.declaredUnitValuePairs?.[0];
        const declaredUnitRequest =
          pair && pair.declaredUnit !== -1
            ? { declaredUnit: pair.declaredUnit, preferred: true, value: pair.value ?? 0, source: 0 }
            : undefined;
        return {
          materialId: id,
          ...(declaredUnitRequest && { declaredUnitRequest }),
          includeMapping: true,
          mappedMaterialId: props.prefillMaterial!.id,
          ...(expectedLifespan.value != null && { lifetime: expectedLifespan.value }),
        };
      });
      await UpdateMaterialPropertiesRequest(project.value!.id, isGroupingByType.value, directPayload);
    }

    emits("toggleModal");
    if (props.prefillMaterial) setInventoryFocus(groupIdentifier.value);
    fetchProjectMaterials(project.value!.id);
    // Forced: a material on a model the dashboard has not seen yet must reach the Models filter.
    fetchDashboard(project.value!.id, true);
  } catch (error) {
    handleApiError(error);
    return;
  } finally {
    isSaving.value = false;
  }
};

const handleRemoveChild = (index: number) => {
  materials.value.splice(index, 1);
};

const handleBimCodeSelect = (value: string) => {
  groupBIMTypeCode.value = value;
};

const updateIsRecycledValue = (newIsRecycled: boolean) => {
  groupIsRecycled.value = newIsRecycled;
};

const modals = useModalStore();

const openLifetimeTable = async () => {
  // Use the country-specific buildingPartServiceLifetime picker when supported.
  if (country.value && !COUNTRIES_WITHOUT_LIFETIME_PICKER.includes(country.value)) {
    const result = await modals.open(
      "MaterialServiceLifetimeSelectorModal",
      {
        country: country.value,
        classificationCode: groupBIMTypeCode.value || undefined,
        materialCategoryId: undefined,
      },
      { stackable: true, wrapperClass: "max-w-2xl h-full" }
    );
    if (result && result.lifetime != null) {
      updateExpectedLifespan(result.lifetime);
    }
    return;
  }

  // Fall back to the legacy LifetimeTable modal for countries without the picker (e.g. Denmark).
  const data = await modals.open(
    "LifetimeTable",
    {
      focusConstructionId: groupBIMTypeCode.value || undefined,
      focusConstructionIdAlternate: groupBIMTypeCode.value ?? "",
    },
    { stackable: true, wrapperClass: "max-w-[90vw] min-h-0" }
  );
  if (data) {
    updateExpectedLifespan(data.value);
  }
};
</script>

<template>
  <div class="text-type-default flex h-full flex-col">
    <div class="flex flex-col gap-1 pb-4">
      <h4 class="t-sh3 text-type-default">Create a material</h4>
      <p class="t-small text-type-muted max-w-prose">
        A material groups one or more types. Add at least one to get started — you can change the list anytime.
      </p>
    </div>

    <div class="rounded-8 bg-slate-0 p-px-20 mb-4 flex flex-col gap-4 border border-slate-200">
      <div class="grid grid-cols-12 items-start gap-4">
        <div class="col-span-6">
          <TextInputNew
            v-model="groupIdentifier"
            :error-msg="
              groupIdentifier.length === 0 && hasValidated
                ? 'Enter a material name'
                : identifierAlreadyExists
                  ? 'Name already exists'
                  : undefined
            "
            placeholder="Material name"
            label="Material name"
            data-cy="material-identifier-input"
            required />
        </div>

        <div class="col-span-6">
          <DropDown
            v-model:value="selectedModelName"
            :options="allModelNames"
            label="Model"
            :required="true"
            placeholder="Select model"
            :error-msg="modelErrorMsg" />
        </div>
      </div>

      <Divider spacing="none" />

      <div class="flex flex-col gap-4">
        <div
          v-if="prefillMaterial"
          class="flex items-end gap-2">
          <div class="w-48">
            <NumberInput
              :value="expectedLifespan"
              color="primary"
              label="Expected lifespan (years)"
              placeholder="Enter lifespan"
              :positive="true"
              :no-decimals="true"
              :max="MAX_LIFESPAN_YEARS"
              :error-msg="lifespanErrorMsg"
              required
              @update:value="updateExpectedLifespan" />
          </div>
          <Button
            color="button"
            label="Lifetime table"
            class="shrink-0"
            @click="openLifetimeTable" />
        </div>

        <!-- Recycled -->
        <div class="flex items-center gap-2">
          <Recycle
            class="icon-sm duration-fast ease-standard transition-colors"
            :class="groupIsRecycled ? 'text-app-blue-600' : 'text-slate-400'"
            :stroke-width="1.5"
            aria-hidden="true" />
          <span class="t-small text-type-default">Reuse</span>
          <Switch
            color="primary"
            :value="groupIsRecycled || false"
            aria-label="Reuse"
            @update:value="(value: boolean) => updateIsRecycledValue(value)" />
        </div>
      </div>
    </div>
    <div class="flex h-full flex-col justify-between">
      <div class="rounded-8">
        <div
          class="gap-px-8 rounded-t-8 t-caption text-type-muted grid grid-cols-18 border border-slate-200 bg-slate-100 px-4 py-3">
          <span class="col-span-6"> Type name </span>
          <span class="col-span-3"> Unit </span>
          <span class="col-span-3"> Unit value </span>

          <span class="col-span-5"> Classification code </span>

          <span class="col-span-1 w-full"></span>
        </div>
        <div class="text-type-muted rounded-b-8 divide-y divide-slate-200 border-x border-b border-slate-200">
          <GroupMaterialRow
            v-for="(child, i) in materials"
            :key="`material-${i}`"
            :child="child"
            :index="i"
            :declared-unit-enums="declaredUnits as unknown as Record<string, DeclaredUnitEnum>"
            :by-type="isGroupingByType"
            :has-validated="hasValidated"
            :speckle-configuration="project!.speckleConfiguration"
            @handle-bim-code-select="handleBimCodeSelect($event)"
            @on-remove="handleRemoveChild(i)" />
        </div>
      </div>
      <!-- One pinned footer holding both actions, sticky inside the modal body’s scroller: the row
           list flows at natural height (no nested scrollbar), and neither action can scroll out of
           view. bg-slate-0 matches the ground this modal sets on its shell, so the strip is invisible
           until it pins. -bottom-6 mirrors the modal body's own pb-6: the sticky rectangle is inset by
           the scroller's padding, so bottom-0 pinned 24px short and rows showed in the gap. -->
      <div class="z-sticky bg-slate-0 sticky -bottom-6 flex w-full items-center justify-between pt-2 pb-1">
        <button
          id="add-child"
          type="button"
          class="focus-ring interactive-active duration-fast ease-standard t-small-bold text-type-muted rounded-8 gap-px-8 flex items-center border border-slate-200 px-3 py-2 transition-colors hover:bg-slate-100"
          @click="createChild">
          <Plus
            class="icon-sm"
            :stroke-width="1.5"
            aria-hidden="true" />
          {{ isGroupingByType ? "Add material" : "Add type" }}
        </button>
        <RevisionGatedButton
          color="primary"
          :label="isSaving ? 'Creating...' : `Create Material`"
          :is-loading="isSaving"
          :disabled="!isFormValid"
          @click="submit" />
      </div>
    </div>
  </div>
</template>
